import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, Camera, Image as ImageIcon } from 'lucide-react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import LetsPlayLoading from '../components/LetsPlayLoading'
import { letsPlayActivityDetailPath, letsPlayCompletionPath } from '../lib/letsPlayActivityNavigation'

export default function LetsPlayMomentPage({ clubCode }) {
  const {t:translate}=useOptionalPrivanaI18n(),t=k=>translate(`racquetPlay.${k}`)
  const { activityId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const racquetJourney = new URLSearchParams(location.search).get('journey') === 'racquet'
  const api = useMemberPortalApi()
  const camera = useRef()
  const library = useRef()
  const objectUrl = useRef(null)
  const alive = useRef(true)
  const generation=useRef(0)
  const [authority, setAuthority] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState('')
  const [caption, setCaption] = useState('')
  const [editingCaption, setEditingCaption] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const hydrate = useCallback(async () => {
    const request=++generation.current
    setLoading(true); setLoadError('')
    try {
      const response = await api.memberPortalApiGet(`/api/members-portal/${clubCode}/lets-play/activities/${activityId}/moment`)
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error)
      if (!alive.current || request!==generation.current) return
      setAuthority(payload.data)
      setCaption(payload.data.moment?.caption || '')
    } catch {
      if (alive.current && request===generation.current) setLoadError('moment_loadError')
    } finally {
      if (alive.current && request===generation.current) setLoading(false)
    }
  }, [activityId, api.memberPortalApiGet, clubCode])

  useEffect(() => {
    alive.current = true
    hydrate()
    return () => { generation.current++;alive.current = false; if (objectUrl.current) URL.revokeObjectURL(objectUrl.current) }
  }, [hydrate])

  const choose = event => {
    const next = event.target.files?.[0]
    event.target.value = ''
    // iOS reports camera/library cancellation as an empty input. Keep it a
    // no-op: no preview, upload, or Moment mutation is attempted.
    if (!next || !alive.current) return
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(next.type) || next.size > 15 * 1024 * 1024) { setError('moment_imageError'); return }
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
    objectUrl.current = URL.createObjectURL(next)
    setFile(next); setPreview(objectUrl.current); setEditingCaption(false); setError('')
  }
  const detail = () => navigate(letsPlayActivityDetailPath(clubCode, authority.activity), { replace: true })
  const leave = () => { if (file && !confirm(t('moment_discard'))) return; detail() }
  const complete = () => navigate(racquetJourney ? `/${clubCode}/lets-play` : authority?.activity?.type === 'match' ? letsPlayActivityDetailPath(clubCode, authority.activity) : letsPlayCompletionPath(clubCode, activityId), { replace: true })

  const save = async () => {
    if (!file || saving || !navigator.onLine) { setError('moment_offline'); return }
    setSaving(true); setError('')
    try {
      const response = await api.memberPortalApiFetch(`/api/members-portal/${clubCode}/lets-play/activities/${activityId}/moment`, { method: 'PUT', headers: { 'Content-Type': file.type, 'X-Moment-Caption': encodeURIComponent(caption.trim()), ...(authority.moment ? { 'If-Match': String(authority.moment.version) } : {}) }, body: file })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error)
      if (alive.current) complete()
    } catch (saveError) {
      if (alive.current) setError(saveError.message === 'moment_version_conflict' ? 'moment_conflict' : 'moment_saveError')
    } finally { if (alive.current) setSaving(false) }
  }
  const saveCaption = async () => {
    setSaving(true); setError('')
    try {
      const response = await api.memberPortalApiPatch(`/api/members-portal/${clubCode}/lets-play/activities/${activityId}/moment`, { caption: caption.trim() }, { headers: { 'If-Match': String(authority.moment.version) } })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error)
      setAuthority(previous => ({ ...previous, moment: payload.data })); setEditingCaption(false)
    } catch (captionError) { setError(captionError.message === 'moment_version_conflict' ? 'moment_conflict' : 'moment_captionError') }
    finally { setSaving(false) }
  }
  const remove = async () => {
    if (!confirm(t('moment_removeConfirm'))) return
    setSaving(true)
    try {
      const response = await api.memberPortalApiDelete(`/api/members-portal/${clubCode}/lets-play/activities/${activityId}/moment`, { headers: { 'If-Match': String(authority.moment.version) } })
      if (response.ok) setAuthority(previous => ({ ...previous, moment: null }))
      else setError('moment_removeError')
    } catch { setError('moment_removeError') }
    finally { setSaving(false) }
  }

  if (loading) return <section className="mp-play-moment"><LetsPlayLoading label={t('moment_opening')} detail={t('moment_access')} /></section>
  if (loadError) return <section className="mp-play-moment"><div className="mp-lets-play__state" role="alert"><h1>{t('moment_unavailable')}</h1><p>{t(loadError)}</p><button onClick={hydrate}>{t('retry')}</button></div></section>
  if (!authority.canMutate) return <section className="mp-play-moment"><header><button onClick={detail} aria-label={t('back')}><ArrowLeft /></button><span>{t('letsPlayBrand')}</span></header><small>{t('moment_label')}</small><h1>{authority.moment ? t('moment_from') : t('moment_none')}</h1>{authority.moment && <><img src={authority.moment.url} alt={authority.moment.caption || t('moment_alt')} />{authority.moment.caption && <p>{authority.moment.caption}</p>}</>}<button className="primary" onClick={detail}>{t('scorecard')}</button></section>

  return <section className="mp-play-moment"><header><button onClick={leave} aria-label={t('back')}><ArrowLeft /></button><span>{t('letsPlayBrand')}</span></header>{!file ? <><small>{authority.moment ? t('moment_your') : t(authority.activity.status === 'session_saved' ? 'sessionSaved' : 'moment_activityComplete')}</small><h1>{authority.moment ? t('moment_remember') : t('moment_capture')}</h1>{authority.moment ? <img src={authority.moment.url} alt={authority.moment.caption || t('moment_alt')} /> : <p>{t('moment_intro')}</p>}
    {authority.moment && (editingCaption ? <><label>{t('moment_editCaption')}<input maxLength="180" value={caption} onChange={event => setCaption(event.target.value)} /></label><button className="primary" disabled={saving} onClick={saveCaption}>{saving ? t('saving') : t('moment_saveCaption')}</button><button onClick={() => { setEditingCaption(false); setCaption(authority.moment.caption || '') }}>{t('cancel')}</button></> : <><button onClick={() => setEditingCaption(true)}>{t('moment_editCaption')}</button><button className="link" disabled={saving} onClick={remove}>{t('moment_remove')}</button></>)}
    <button className="primary" onClick={() => camera.current.click()}><Camera /> {t('moment_take')}</button><button onClick={() => library.current.click()}><ImageIcon /> {authority.moment ? t('moment_replace') : t('moment_choose')}</button><button className="link" onClick={complete}>{t('moment_later')}</button></> : <><small>{t('moment_your')}</small><h1>{t('moment_keep')}</h1><img src={preview} alt={t('moment_preview')} /><label>{t('moment_caption')}<input maxLength="180" value={caption} onChange={event => setCaption(event.target.value)} placeholder={t('moment_captionExample')} /></label>{error && <p role="alert">{t(error)}</p>}<button className="primary" disabled={saving} onClick={save}>{saving ? t('moment_preparing') : t('moment_save')}</button><button disabled={saving} onClick={() => library.current.click()}>{t('moment_another')}</button><button className="link" onClick={complete}>{t('moment_later')}</button></>}
    {!file && error && <p role="alert">{t(error)}</p>}<input ref={camera} hidden type="file" accept="image/jpeg,image/png,image/webp" capture="user" onChange={choose} /><input ref={library} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={choose} />
  </section>
}
